import React from 'react';
import { Container, Box, Alert } from '@mui/material';
import {
  hasOnlyButtonGroupFields,
  hasSomeContentFields,
  hasRegularInputFields,
} from '../../utils/questionnaireFieldUtils';

export interface WizardSectionContentProps {
  currentSection: any;
  sectionIndex: number;
  isMobile: boolean;
  sectionPageIndex: number;
  formValues: Record<string, any>;
  cameFromBackNav: boolean;
  hasMorePages: () => boolean;
  getFieldsForCurrentPage: () => any[];
  renderField: (field: any) => React.ReactNode;
  /** Error message to display inside content (optional) */
  error?: string | null;
  /** Whether the wizard is currently in a review step (Generic wizard only) */
  isReviewStep?: boolean;
  /** Body content for the review step (Generic wizard only) */
  reviewBodyContent?: React.ReactNode;
  /** Footer (button) content for the review step (Generic wizard only) */
  reviewFooterContent?: React.ReactNode;
  /**
   * When true, the default layout branch renders all section button fields
   * directly (Generic wizard — no pagination interference).
   * When false (default), uses the auto-advance/pagination-aware computed
   * buttonFields (Public wizard).
   */
  useActualButtonsInDefault?: boolean;
}

/**
 * Shared main content area for both wizard variants. Computes layout
 * variants (button-group-only, separated buttons, default) and renders the
 * appropriate JSX, accepting a `renderField` callback so each wizard keeps
 * ownership of its field props.
 */
export const WizardSectionContent: React.FC<WizardSectionContentProps> = ({
  currentSection,
  sectionIndex,
  isMobile,
  sectionPageIndex,
  formValues,
  cameFromBackNav,
  hasMorePages,
  getFieldsForCurrentPage,
  renderField,
  error,
  isReviewStep = false,
  reviewBodyContent,
  reviewFooterContent,
  useActualButtonsInDefault = false,
}) => {
  if (!currentSection && !isReviewStep) return null;

  // ── Layout flags ────────────────────────────────────────────────────────
  const isButtonGroupOnlySection = currentSection && !isReviewStep
    ? hasOnlyButtonGroupFields(currentSection) : false;
  const hasContentFields = currentSection && !isReviewStep
    ? hasSomeContentFields(currentSection) : false;
  const hasRegularFields = currentSection && !isReviewStep
    ? hasRegularInputFields(currentSection) : false;
  const hasContainerOrLineFields = currentSection && !isReviewStep
    ? currentSection.fields.some((f: any) => f.type === 'container' || f.type === 'line')
    : false;
  const hasOnlyContentFields =
    hasContentFields && !hasRegularFields && !hasContainerOrLineFields && !isButtonGroupOnlySection;
  const isPaymentSummarySection = currentSection && currentSection.title === 'Payment Summary';
  const shouldSeparateButtons =
    isMobile && (hasContentFields || hasContainerOrLineFields || isPaymentSummarySection);
  const shouldRemoveParentBox = isMobile && isButtonGroupOnlySection;
  const shouldHaveWhiteBox =
    !isMobile ||
    (((hasRegularFields && !hasContainerOrLineFields) || hasOnlyContentFields) &&
      !(isMobile && isPaymentSummarySection));

  // ── Pagination / auto-advance ────────────────────────────────────────────
  const isIntermediatePage = isMobile && !isReviewStep && hasMorePages();

  const questionFields = currentSection
    ? currentSection.fields.filter((f: any) =>
        f.type !== 'button' && f.type !== 'content' && f.type !== 'info-banner'
      )
    : [];
  const questionCount = questionFields.length;
  const start = sectionPageIndex * 6;
  const end = start + 6;
  const currentPageFields = questionFields.slice(start, end);

  const hasFilledFields = currentPageFields.some((field: any) => {
    const value = formValues[field.name];
    return (
      value !== undefined && value !== null && value !== '' &&
      !(Array.isArray(value) && value.length === 0)
    );
  });

  const shouldEnableAutoAdvance =
    isMobile && (questionCount === 6 || (questionCount > 6 && isIntermediatePage));

  const actualButtonFields = currentSection
    ? currentSection.fields.filter((field: any) => field.type === 'button')
    : [];

  // Computed buttonFields with pagination / auto-advance logic
  const buttonFields: any[] = isIntermediatePage && !hasFilledFields
    ? []
    : isIntermediatePage && hasFilledFields && cameFromBackNav
    ? [{ type: 'button', name: 'pagination-continue', label: 'Continue', action: 'pagination-next' }]
    : isIntermediatePage && hasFilledFields && !cameFromBackNav
    ? []
    : shouldEnableAutoAdvance && !cameFromBackNav
    ? []
    : actualButtonFields;

  // Paginated non-info-banner fields (used for first two layout branches and
  // for the default branch in the Public wizard)
  const nonInfoBannerFields = isMobile && currentSection
    ? getFieldsForCurrentPage().filter((field: any) => field.type !== 'info-banner')
    : getFieldsForCurrentPage();

  // Info-banner fields separated out — rendered above the white card on all viewports
  const infoBannerFields = currentSection
    ? currentSection.fields.filter((field: any) => field.type === 'info-banner')
    : [];

  // ── Default-branch helpers ───────────────────────────────────────────────
  // Generic passes all section fields; Public uses paginated nonInfoBannerFields
  const defaultContentFields = isReviewStep || !currentSection
    ? []
    : useActualButtonsInDefault
    ? currentSection.fields.filter((f: any) => f.type !== 'button' && f.type !== 'info-banner')
    : nonInfoBannerFields.filter((f: any) => f.type !== 'button');

  // Generic re-declares buttonFields in default branch; Public uses computed
  const defaultBranchButtonFields = useActualButtonsInDefault
    ? (currentSection ? currentSection.fields.filter((f: any) => f.type === 'button') : [])
    : buttonFields;

  // ── Shared sub-renderers ─────────────────────────────────────────────────
  const renderInfoBanners = () =>
    infoBannerFields.length > 0 ? (
      <Container maxWidth="md" sx={{ pt: 0, pb: 1.5 }}>
        <Box key={`section-info-banners-${sectionIndex}`}>
          {infoBannerFields.map((field: any) => (
            <React.Fragment key={`section-${sectionIndex}-info-banner-${field.name}`}>
              {renderField(field)}
            </React.Fragment>
          ))}
        </Box>
      </Container>
    ) : null;

  const renderButtonRow = (fields: any[], keyPrefix: string) =>
    fields.length > 0 ? (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        {fields.map((btnField: any) => (
          <React.Fragment key={`section-${sectionIndex}-${keyPrefix}-${btnField.name}`}>
            {renderField(btnField)}
          </React.Fragment>
        ))}
      </Box>
    ) : null;

  const renderOutsideButtonContainer = (fields: any[], keyPrefix = 'button') =>
    fields.length > 0 ? (
      <Container maxWidth="md" sx={{ pb: { xs: 2, sm: 3 }, pt: 0 }}>
        <Box sx={{ maxWidth: '700px', mx: 'auto', width: '100%' }}>
          {renderButtonRow(fields, keyPrefix)}
        </Box>
      </Container>
    ) : null;

  const renderErrorAboveButtons = () =>
    error ? (
      <Container maxWidth="md" sx={{ pb: 1, pt: 0 }}>
        <Box sx={{ maxWidth: '700px', mx: 'auto', width: '100%' }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    ) : null;

  // ── Branch A: mobile button-group only — no parent white box ────────────
  if (shouldSeparateButtons && shouldRemoveParentBox) {
    return (
      <>
        {renderInfoBanners()}

        <Container maxWidth="md" sx={{ pt: 0, pb: { xs: 1.5, sm: 2, md: 6 }, flex: { xs: 1, md: '0 1 auto' } }}>
          <Box key={`section-content-${sectionIndex}`}>
            {nonInfoBannerFields
              .filter((field: any) => field.type !== 'button')
              .map((field: any) => (
                <React.Fragment key={`section-${sectionIndex}-field-${field.name}`}>
                  {renderField(field)}
                </React.Fragment>
              ))}
          </Box>
        </Container>

        {renderErrorAboveButtons()}
        {renderOutsideButtonContainer(buttonFields)}
      </>
    );
  }

  // ── Branch B: mobile content fields — parent white box + outside buttons ─
  if (shouldSeparateButtons && !shouldRemoveParentBox) {
    return (
      <>
        {renderInfoBanners()}

        <Container maxWidth="md" sx={{ pt: 0, pb: { xs: 1.5, sm: 2, md: 6 }, flex: { xs: 1, md: '0 1 auto' } }}>
          <Box
            sx={{
              maxWidth: '700px',
              mx: 'auto',
              width: '100%',
              ...(!isMobile || (hasOnlyContentFields && !isPaymentSummarySection)
                ? {
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    p: { xs: 1.5, sm: 2, md: 5 },
                  }
                : {}),
              '& .MuiInputBase-root': { height: { xs: '39px', md: '51px' } },
            }}
          >
            <Box key={`section-content-${sectionIndex}`}>
              {nonInfoBannerFields
                .filter((field: any) => field.type !== 'button')
                .map((field: any) => (
                  <React.Fragment key={`section-${sectionIndex}-field-${field.name}`}>
                    {renderField(field)}
                  </React.Fragment>
                ))}
            </Box>
          </Box>
        </Container>

        {renderErrorAboveButtons()}
        {renderOutsideButtonContainer(buttonFields)}
      </>
    );
  }

  // ── Branch C: default — desktop or regular-input sections ───────────────
  return (
    <>
      {renderInfoBanners()}

      <Container maxWidth="md" sx={{ pt: 0, pb: { xs: 1.5, sm: 2, md: 6 }, flex: { xs: 1, md: '0 1 auto' } }}>
        <Box
          sx={{
            ...(shouldHaveWhiteBox && {
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              p: { xs: 1.5, sm: 2, md: 5 },
            }),
            maxWidth: '700px',
            mx: 'auto',
            width: '100%',
            '& .MuiInputBase-root': { height: { xs: '39px', md: '51px' } },
          }}
        >
          {/* Form fields or review content */}
          <Box key={`section-content-${sectionIndex}`}>
            {!isReviewStep ? (
              defaultContentFields.map((field: any) => (
                <React.Fragment key={`section-${sectionIndex}-field-${field.name}`}>
                  {renderField(field)}
                </React.Fragment>
              ))
            ) : (
              reviewBodyContent
            )}
          </Box>

          {/* Footer buttons inside white box (desktop or mobile-with-regular-fields) */}
          {(!isMobile || hasRegularFields) && (() => {
            if (!isReviewStep && defaultBranchButtonFields.length > 0) {
              return (
                <Box sx={{ pt: { xs: 0, md: 3 } }}>
                  {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                  {renderButtonRow(defaultBranchButtonFields, 'button')}
                </Box>
              );
            }
            if (isReviewStep && reviewFooterContent) {
              return (
                <Box sx={{ pt: 3 }}>
                  {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    {reviewFooterContent}
                  </Box>
                </Box>
              );
            }
            return null;
          })()}
        </Box>
      </Container>

      {/* Footer buttons outside white box (mobile only, no regular fields) */}
      {isMobile && !hasRegularFields && (() => {
        if (!isReviewStep && defaultBranchButtonFields.length > 0) {
          return (
            <>
              {renderErrorAboveButtons()}
              {renderOutsideButtonContainer(defaultBranchButtonFields, 'button-mobile')}
            </>
          );
        }
        if (isReviewStep && reviewFooterContent) {
          return (
            <>
              {renderErrorAboveButtons()}
              <Container maxWidth="md" sx={{ pb: { xs: 2, sm: 3 }, pt: 0 }}>
                <Box sx={{ maxWidth: '700px', mx: 'auto', width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    {reviewFooterContent}
                  </Box>
                </Box>
              </Container>
            </>
          );
        }
        return null;
      })()}
    </>
  );
};
